/**
 * CreationModePanel — Full creation UI for pending (new) pipeline nodes.
 *
 * Renders when a user clicks "Add Node" and a pending placeholder is on the canvas.
 * Handles: code editing, table selection, preview, agent code assist, and create+execute.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import {
  useCreateOperation,
  useWorkspaceQuery,
  useSendWorkspaceMessage,
} from '../../hooks/useWorkspaceSession';
import type { WorkspaceTable } from '../../services/workspaceApi';
import { previewPythonTransform } from '../../services/workspaceApi';
import type { PipelineNode } from './DAGCanvas';
import type { AddNodeType } from './AddNodeFAB';
import { getApiErrorMessage } from '../../services/apiErrors';
import { WORKSPACE_MONACO_EDITOR_DEFAULTS } from './workspaceMonacoDefaults';
import TableBrowser from './TableBrowser';
import { useMonacoTableCompletion } from './hooks/useMonacoTableCompletion';
import { getNodeColor, getNodeIcon, getTypeLabel } from './nodeConstants';

// ── Types ────────────────────────────────────────────────────────────

interface CreationModePanelProps {
  sessionId: string;
  pendingNodeType: AddNodeType;
  selectedNode: PipelineNode;
  workspaceTables: WorkspaceTable[];
  onCancelPending: () => void;
  onPendingCreated: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  isBusy?: boolean;
  restoredCreationState?: {
    creationCode: string;
    outputName: string;
    inputRefs: string[];
    creationExploratory: boolean;
  } | null;
  onCreationStateChange?: (state: {
    code?: string;
    outputName?: string;
    inputRefs?: string[];
    exploratory?: boolean;
  }) => void;
}

// ── Component ────────────────────────────────────────────────────────

const CreationModePanel: React.FC<CreationModePanelProps> = ({
  sessionId,
  pendingNodeType,
  selectedNode,
  workspaceTables,
  onCancelPending,
  onPendingCreated,
  isFullscreen,
  onToggleFullscreen,
  isBusy,
  restoredCreationState,
  onCreationStateChange,
}) => {
  const theme = useTheme();

  // ── State ──────────────────────────────────────────────────────────
  const [creationCode, setCreationCode] = useState('');
  const [creationOutputName, setCreationOutputName] = useState('');
  const creationEditorRef = React.useRef<any>(null);
  const [creationExploratory, setCreationExploratory] = useState(false);
  const [creationInputRefs, setCreationInputRefs] = useState<string[]>([]);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [creationLoading, setCreationLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<{
    columns: string[];
    rows: Record<string, any>[];
    row_count: number;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [agentInput, setAgentInput] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);

  // Monaco IntelliSense state
  const [creationMonaco, setCreationMonaco] = useState<any>(null);
  const [creationEditorInst, setCreationEditorInst] = useState<any>(null);

  // ── Hooks ──────────────────────────────────────────────────────────
  const createOp = useCreateOperation(sessionId);
  const workspaceQuery = useWorkspaceQuery(sessionId);
  const sendAgentMessage = useSendWorkspaceMessage(sessionId);

  const creationLanguage: 'sql' | 'python' =
    pendingNodeType === 'python_transform' ? 'python' : 'sql';

  useMonacoTableCompletion(
    creationMonaco,
    creationEditorInst,
    workspaceTables,
    creationLanguage,
  );

  // ── Creation-mode agent helper ────────────────────────────────────
  const handleCreationAgentAsk = useCallback(
    async (instruction: string) => {
      if (!sessionId || agentLoading) return;
      const lang =
        pendingNodeType === 'python_transform' ? 'Python' : 'SQL';
      const tableContext =
        creationInputRefs.length > 0
          ? `\nSelected input tables: ${creationInputRefs.join(', ')}`
          : '';
      const existingCode = creationCode.trim();
      const codeContext =
        existingCode &&
        existingCode !==
          '-- Write SQL against workspace tables\nSELECT *\nFROM \nWHERE 1=1' &&
        existingCode !==
          "# Input: df (pandas DataFrame from first selected table)\n# Assign result to 'result'\nresult = df"
          ? `\nCurrent code:\n\`\`\`${lang.toLowerCase()}\n${existingCode}\n\`\`\``
          : '';

      const prompt = [
        `[CODE_ONLY] The user is authoring a new ${lang} transform in the editor.`,
        `Do NOT execute the code or create a new transform. Only provide the ${lang} code in a single fenced code block.`,
        `The user will review and execute it themselves.${tableContext}${codeContext}`,
        `\nUser instruction: ${instruction}`,
      ].join('\n');

      setAgentLoading(true);
      try {
        const result = await sendAgentMessage.mutateAsync(prompt);
        const content = result.content || '';
        const codeBlockMatch = content.match(
          /```(?:sql|python|py)?\s*\n([\s\S]*?)```/,
        );
        if (codeBlockMatch?.[1]) {
          setCreationCode(codeBlockMatch[1].trimEnd());
        } else {
          const trimmed = content.trim();
          if (trimmed && !trimmed.startsWith('#') && trimmed.includes('\n')) {
            setCreationCode(trimmed);
          }
        }
      } catch {
        // Error handled by mutation's onError; message appears in chat
      } finally {
        setAgentLoading(false);
      }
    },
    [
      sessionId,
      agentLoading,
      pendingNodeType,
      creationInputRefs,
      creationCode,
      sendAgentMessage,
    ],
  );

  // ── State initialisation & restoration ─────────────────────────────
  useEffect(() => {
    const pendingMeta = selectedNode as PipelineNode & {
      _pendingOutputName?: string;
      _initialSql?: string;
    };
    setCreationError(null);
    setPreviewResult(null);
    setPreviewError(null);

    if (restoredCreationState) {
      setCreationCode(restoredCreationState.creationCode);
      setCreationOutputName(restoredCreationState.outputName);
      setCreationExploratory(restoredCreationState.creationExploratory);
      setCreationInputRefs(restoredCreationState.inputRefs);
    } else {
      setCreationOutputName(pendingMeta._pendingOutputName || 'transform_1');
      setCreationExploratory(false);
      setCreationInputRefs(selectedNode?.inputRefs || []);
      if (pendingNodeType === 'sql_transform') {
        setCreationCode(
          pendingMeta._initialSql ||
            '-- Write SQL against workspace tables\nSELECT *\nFROM \nWHERE 1=1',
        );
      } else if (pendingNodeType === 'python_transform') {
        setCreationCode(
          "# Input: df (pandas DataFrame from first selected table)\n# Assign result to 'result'\nresult = df",
        );
      } else {
        setCreationCode('');
      }
    }
  }, [pendingNodeType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync creation form state to sessionStorage on changes
  useEffect(() => {
    if (!onCreationStateChange) return;
    onCreationStateChange({
      code: creationCode,
      outputName: creationOutputName,
      inputRefs: creationInputRefs,
      exploratory: creationExploratory,
    });
  }, [
    creationCode,
    creationOutputName,
    creationInputRefs,
    creationExploratory,
    onCreationStateChange,
  ]);

  // Update scaffold when input tables change
  useEffect(() => {
    if (creationInputRefs.length === 0) return;
    if (pendingNodeType === 'sql_transform') {
      setCreationCode((prev) => {
        if (prev.startsWith('-- Write SQL') || prev === '') {
          const tables = creationInputRefs;
          return `-- Write SQL against workspace tables\nSELECT *\nFROM ${tables[0]}\nWHERE 1=1`;
        }
        return prev;
      });
    }
  }, [creationInputRefs, pendingNodeType]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    if (!creationCode.trim()) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      if (pendingNodeType === 'python_transform') {
        const inputTable = creationInputRefs[0] || '';
        if (!inputTable) {
          setPreviewError(
            'Select at least one input table for Python preview.',
          );
          return;
        }
        const result = await previewPythonTransform(
          sessionId,
          creationCode,
          inputTable,
          100,
        );
        setPreviewResult({
          columns: result.columns,
          rows: result.rows,
          row_count: result.row_count,
        });
      } else {
        const result = await workspaceQuery.mutateAsync({
          sql: creationCode,
          limit: 100,
        });
        setPreviewResult({
          columns: result.columns,
          rows: result.rows,
          row_count: result.row_count,
        });
      }
    } catch (err: unknown) {
      setPreviewError(getApiErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  }, [
    creationCode,
    workspaceQuery,
    pendingNodeType,
    creationInputRefs,
    sessionId,
  ]);

  const handleCreateAndExecute = useCallback(async () => {
    if (!creationCode.trim() || !creationOutputName.trim()) return;
    setCreationLoading(true);
    setCreationError(null);
    try {
      const configKey =
        pendingNodeType === 'sql_transform'
          ? 'sql'
          : pendingNodeType === 'python_transform'
            ? 'function_code'
            : 'config';
      await createOp.mutateAsync({
        operation_type: pendingNodeType!,
        config:
          pendingNodeType === 'fuzzy_match'
            ? JSON.parse(creationCode || '{}')
            : { [configKey]: creationCode },
        output_name: creationOutputName,
        input_refs: creationInputRefs,
        exploratory: creationExploratory,
      });
      onPendingCreated?.();
    } catch (err: unknown) {
      setCreationError(getApiErrorMessage(err));
    } finally {
      setCreationLoading(false);
    }
  }, [
    creationCode,
    creationOutputName,
    creationInputRefs,
    creationExploratory,
    pendingNodeType,
    createOp,
    onPendingCreated,
  ]);

  // ── Derived values ─────────────────────────────────────────────────
  const creationColor = getNodeColor(pendingNodeType);
  const creationIcon = getNodeIcon(pendingNodeType);
  const creationLabel = getTypeLabel(pendingNodeType);
  const codeLanguage =
    pendingNodeType === 'python_transform' ? 'python' : 'sql';
  const isFuzzyMatch = pendingNodeType === 'fuzzy_match';
  const canPreview = !isFuzzyMatch && creationCode.trim().length > 0;
  const canCreate =
    creationCode.trim().length > 0 && creationOutputName.trim().length > 0;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
          bgcolor: alpha(creationColor, 0.04),
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 0.75,
            bgcolor: creationColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {creationIcon}
        </Box>
        <Typography
          variant="subtitle2"
          noWrap
          fontWeight={600}
          sx={{ fontSize: 12 }}
        >
          New {creationLabel}
        </Typography>
        <Chip
          label="Creating"
          size="small"
          sx={{
            fontSize: 10,
            height: 20,
            fontWeight: 600,
            bgcolor: alpha(creationColor, 0.1),
            color: creationColor,
            border: 'none',
            animation: 'creationPulse 2s ease-in-out infinite',
            '@keyframes creationPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.6 },
            },
          }}
        />
        <Box sx={{ flex: 1 }} />
        {/* Ask Agent */}
        <TextField
          size="small"
          variant="outlined"
          placeholder={
            agentLoading
              ? 'Agent is writing code...'
              : 'Ask agent to help write code...'
          }
          value={agentInput}
          disabled={agentLoading}
          onChange={(e) => setAgentInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && agentInput.trim() && !agentLoading) {
              handleCreationAgentAsk(agentInput.trim());
              setAgentInput('');
            }
          }}
          InputProps={{
            endAdornment: agentLoading ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : (
              <IconButton
                size="small"
                disabled={!agentInput.trim()}
                onClick={() => {
                  if (agentInput.trim()) {
                    handleCreationAgentAsk(agentInput.trim());
                    setAgentInput('');
                  }
                }}
                sx={{ p: 0.25 }}
              >
                <SendIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ),
            sx: { fontSize: 11, height: 28, pr: 0.5 },
          }}
          sx={{ flex: 1, maxWidth: 320, mr: 0.5 }}
        />
        {onToggleFullscreen && (
          <Tooltip
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen editor'}
          >
            <IconButton
              size="small"
              onClick={onToggleFullscreen}
              sx={{ p: 0.5 }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon sx={{ fontSize: 16 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          onClick={onCancelPending}
          sx={{
            fontSize: 10,
            textTransform: 'none',
            py: 0,
            px: 1,
            minHeight: 26,
          }}
        >
          Cancel
        </Button>
        <IconButton size="small" onClick={onCancelPending} sx={{ ml: 0.5 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Code editor with table browser */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!isFuzzyMatch ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* Table browser (left panel) */}
            <TableBrowser
              tables={workspaceTables}
              onInsert={(text) => {
                const editor = creationEditorRef.current;
                if (editor) {
                  const position = editor.getPosition();
                  if (position) {
                    editor.executeEdits('table-browser', [
                      {
                        range: {
                          startLineNumber: position.lineNumber,
                          startColumn: position.column,
                          endLineNumber: position.lineNumber,
                          endColumn: position.column,
                        },
                        text,
                      },
                    ]);
                    editor.focus();
                  }
                } else {
                  setCreationCode((prev) => prev + text);
                }
              }}
              referencedTables={(() => {
                const refs = new Set<string>();
                if (codeLanguage === 'sql') {
                  const matches = creationCode.match(
                    /(?:FROM|JOIN)\s+"?([^"\s,;]+)"?/gi,
                  );
                  matches?.forEach((m) => {
                    const name = m
                      .replace(/^(?:FROM|JOIN)\s+"?/i, '')
                      .replace(/"?\s*$/, '');
                    if (name) refs.add(name);
                  });
                }
                creationInputRefs.forEach((r) => refs.add(r));
                return refs;
              })()}
              selectedInputTables={creationInputRefs}
              onInputTablesChange={(tables) => setCreationInputRefs(tables)}
            />
            {/* Monaco editor (right) */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Editor
                height="100%"
                language={codeLanguage}
                value={creationCode}
                onChange={(value) => setCreationCode(value || '')}
                theme="vs-dark"
                onMount={(editor, monaco) => {
                  creationEditorRef.current = editor;
                  setCreationMonaco(monaco);
                  setCreationEditorInst(editor);
                }}
                wrapperProps={{
                  'data-testid': 'node-inspector-creation-code-editor',
                  'data-editor-state': 'editable',
                }}
                options={
                  {
                    ...WORKSPACE_MONACO_EDITOR_DEFAULTS,
                    ariaLabel:
                      codeLanguage === 'sql'
                        ? 'New SQL transform code editor'
                        : 'New Python transform code editor',
                    minimap: { enabled: false },
                    fontSize: 13,
                    fontFamily: '"Fira Code", "Roboto Mono", monospace',
                    lineNumbers: 'on' as const,
                    scrollBeyondLastLine: false,
                    wordWrap: 'on' as const,
                    padding: { top: 8 },
                    renderLineHighlight: 'all' as const,
                    scrollbar: { verticalScrollbarSize: 8 },
                  } as any
                }
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, p: 1.5, overflow: 'auto' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11, mb: 1, display: 'block' }}
            >
              Fuzzy match configuration (JSON):
            </Typography>
            <Editor
              height="200px"
              language="json"
              value={
                creationCode ||
                '{\n  "left_on": [],\n  "right_on": [],\n  "threshold": 0.8\n}'
              }
              onChange={(value) => setCreationCode(value || '')}
              theme="vs-dark"
              wrapperProps={{
                'data-testid': 'node-inspector-creation-fuzzy-json-editor',
                'data-editor-state': 'editable',
              }}
              options={
                {
                  ...WORKSPACE_MONACO_EDITOR_DEFAULTS,
                  ariaLabel: 'Fuzzy match configuration JSON editor',
                  minimap: { enabled: false },
                  fontSize: 13,
                  fontFamily: '"Fira Code", "Roboto Mono", monospace',
                  lineNumbers: 'on' as const,
                  scrollBeyondLastLine: false,
                  wordWrap: 'on' as const,
                  padding: { top: 8 },
                } as any
              }
            />
          </Box>
        )}

        {/* Preview results */}
        {previewResult && (
          <Box
            sx={{
              maxHeight: 150,
              overflow: 'auto',
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: alpha('#3b82f6', 0.02),
            }}
          >
            <Box
              sx={{
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Chip
                label="Preview"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: 9, height: 16 }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: 10, color: 'text.secondary' }}
              >
                {previewResult.row_count} rows ·{' '}
                {previewResult.columns.length} columns
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 120 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {previewResult.columns.slice(0, 8).map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          py: 0.25,
                          px: 1,
                          whiteSpace: 'nowrap',
                          bgcolor: '#f8fafc',
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                    {previewResult.columns.length > 8 && (
                      <TableCell
                        sx={{
                          fontSize: 10,
                          py: 0.25,
                          px: 1,
                          bgcolor: '#f8fafc',
                        }}
                      >
                        +{previewResult.columns.length - 8}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewResult.rows.slice(0, 5).map((row, i) => (
                    <TableRow key={i}>
                      {previewResult.columns.slice(0, 8).map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            fontSize: 10,
                            py: 0.25,
                            px: 1,
                            whiteSpace: 'nowrap',
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row[col] != null ? String(row[col]) : ''}
                        </TableCell>
                      ))}
                      {previewResult.columns.length > 8 && (
                        <TableCell sx={{ fontSize: 10, py: 0.25, px: 1 }}>
                          …
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Errors */}
        {(creationError || previewError) && (
          <Box sx={{ px: 1.5, py: 0.5 }}>
            <Alert
              severity="error"
              variant="outlined"
              onClose={() => {
                setCreationError(null);
                setPreviewError(null);
              }}
              sx={{
                py: 0.75,
                '& .MuiAlert-message': {
                  fontSize: 12,
                  width: '100%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                },
              }}
            >
              {creationError || previewError}
            </Alert>
          </Box>
        )}
      </Box>

      {/* Footer: output name + exploratory + actions */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: alpha(theme.palette.grey[100], 0.5),
        }}
      >
        <TextField
          size="small"
          label="Output Name"
          value={creationOutputName}
          onChange={(e) =>
            setCreationOutputName(
              e.target.value.replace(/\s+/g, '_').toLowerCase(),
            )
          }
          sx={{
            width: 180,
            '& .MuiInputBase-root': { fontSize: 12, height: 32 },
            '& .MuiInputLabel-root': { fontSize: 11 },
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={creationExploratory}
              onChange={(e) => setCreationExploratory(e.target.checked)}
              sx={{ p: 0.25 }}
            />
          }
          label={<Typography sx={{ fontSize: 11 }}>Exploratory</Typography>}
          sx={{ mr: 0 }}
        />
        <Box sx={{ flex: 1 }} />
        {canPreview && (
          <Button
            size="small"
            variant="outlined"
            onClick={handlePreview}
            disabled={previewLoading || isBusy}
            sx={{
              fontSize: 11,
              textTransform: 'none',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
            }}
          >
            {previewLoading ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : null}
            Preview
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={handleCreateAndExecute}
          disabled={!canCreate || creationLoading || isBusy}
          sx={{
            fontSize: 11,
            textTransform: 'none',
            py: 0.25,
            px: 1.5,
            minHeight: 28,
          }}
        >
          {creationLoading ? (
            <CircularProgress size={14} sx={{ mr: 0.5, color: '#fff' }} />
          ) : null}
          {creationLoading ? 'Creating...' : 'Create & Execute'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreationModePanel;
